import React from 'react';
import { useDispatch } from 'react-redux';
import { getDeviceData, postDeviceData } from '../../redux/actions/userActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import DeviceStyles from './Device.module.css';

const FRONTEND_DEVICE_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	COMMAND_ACTION_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildDeviceSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const DeviceScreen = () => {
	const dispatch = useDispatch();

	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ deviceData, setDeviceData ] = React.useState({});
	const [ deviceDataLoading, setDeviceDataLoading ] = React.useState('');
	const [ selectedWatcher, setSelectedWatcher ] = React.useState('');
	const [ watchers, setWatchers ] = React.useState([]);
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);

	const setDeviceSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildDeviceSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearDeviceSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [ supportErrorMessage ]);

	const getData = React.useCallback(
		async () => {
			setDataLoading(true);
			try {
				const data = await dispatch(getDeviceData());
				if (data && data.status) {
					setDeviceData(data);
					clearDeviceSupportError(true);

					if (data.allowedWatchers) {
						const sortedWatchers = [ ...data.allowedWatchers ].sort((a, b) => {
							const num = (s) => {
								const m = String(s).match(/\d+/);
								return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
							};
							const na = num(a), nb = num(b);
							if (na !== nb) return na - nb;
							return String(a).localeCompare(String(b));
						});
						setWatchers(sortedWatchers);
						if (sortedWatchers.length > 0) {
							setSelectedWatcher(sortedWatchers[0]);
						}
					}
				} else {
					setDeviceData({});
					setWatchers([]);
					setSelectedWatcher('');
					setDeviceSupportError(
						FRONTEND_DEVICE_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Device Page / Initial Data Load Failed',
						data?.msg || (typeof data === 'string' ? data : 'Device request did not return success')
					);
				}
			} catch (error) {
				setDeviceData({});
				setWatchers([]);
				setSelectedWatcher('');
				setDeviceSupportError(
					FRONTEND_DEVICE_ERROR_CODES.INITIAL_LOAD_FAILED,
					'Device Page / Initial Data Request Error',
					error?.message || String(error)
				);
			} finally {
				setDataLoading(false);
			}
		},
		[ clearDeviceSupportError, dispatch, setDeviceSupportError ]
	);

	React.useEffect(
		() => {
			getData();
		},
		[ getData ]
	);

	const handlePostDeviceData = async (btnType, loadingType) => {
		const formData = new FormData();
		formData.append('btn', btnType);
		
		// Add the watcherID for command operations
		if (['RestartServices', 'PullLatestIDSRules', 'PullLatestDeniedList', 'RebootSystem'].includes(btnType)) {
			formData.append('watcherID', selectedWatcher);
		}
		
		setDeviceDataLoading(loadingType);
		try {
			const data = await dispatch(postDeviceData(formData));
			if (data && data.status) {
				setDeviceData(data);
				clearDeviceSupportError(true);
			} else {
				setDeviceSupportError(
					FRONTEND_DEVICE_ERROR_CODES.COMMAND_ACTION_FAILED,
					`Device Page / ${btnType} Failed`,
					data?.msg || (typeof data === 'string' ? data : 'Device command request did not return success')
				);
			}
		} catch (error) {
			setDeviceSupportError(
				FRONTEND_DEVICE_ERROR_CODES.COMMAND_ACTION_FAILED,
				`Device Page / ${btnType} Request Error`,
				error?.message || String(error)
			);
		} finally {
			setDeviceDataLoading('');
		}
	};

	return (
		<Content currentMenu='system' headerTitle='Device'>
			{dataLoading ? (
				<Loader />
			) : deviceData && Object.keys(deviceData).length === 0 ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={DeviceStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={DeviceStyles.supportErrorCode}>{supportErrorMessage}</code>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={DeviceStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={DeviceStyles.copySupportErrorButton}
									onClick={() => clearDeviceSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
				</div>
			) : (
				<>
					{supportErrorMessage && (
						<div className={DeviceStyles.supportErrorBanner}>
							<div className={DeviceStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={DeviceStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={DeviceStyles.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={DeviceStyles.copySupportErrorButton}
									onClick={() => clearDeviceSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					<div className={DeviceStyles.content}>
						<h3 className={DeviceStyles.heading}>
							Watcher Commands
						</h3>
						
						{watchers && watchers.length > 0 ? (
							<>
								<div className={DeviceStyles.watcherSelector}>
									<label>Select Watcher:</label>
									<select 
										value={selectedWatcher} 
										onChange={(e) => setSelectedWatcher(e.target.value)}
										disabled={deviceDataLoading !== ''}
									>
										{watchers.map((watcher) => (
											<option key={watcher} value={watcher}>{watcher}</option>
										))}
									</select>
								</div>
								
								<div className={DeviceStyles.btns}>
									{deviceDataLoading === '' ? (
										<button
											onClick={() => handlePostDeviceData('RestartServices', 'restartingServices')}
											disabled={deviceDataLoading !== '' || !selectedWatcher}
											className={DeviceStyles.primaryBtn}>
											Restart Services
										</button>
									) : (
										<button className={DeviceStyles.loadingBtn}>
											{deviceDataLoading === 'restartingServices' ? 'Restarting Services' : 'Restart Services'}
										</button>
									)}

									{deviceDataLoading === '' ? (
										<button
											onClick={() => handlePostDeviceData('PullLatestIDSRules', 'pullingIDSRules')}
											disabled={deviceDataLoading !== '' || !selectedWatcher}
											className={DeviceStyles.primaryBtn}>
											Pull Latest IDS Rules
										</button>
									) : (
										<button className={DeviceStyles.loadingBtn}>
											{deviceDataLoading === 'pullingIDSRules' ? 'Pulling Latest IDS Rules' : 'Pull Latest IDS Rules'}
										</button>
									)}
									
									{deviceDataLoading === '' ? (
										<button
											onClick={() => handlePostDeviceData('PullLatestDeniedList', 'pullingDeniedList')}
											disabled={deviceDataLoading !== '' || !selectedWatcher}
											className={DeviceStyles.primaryBtn}>
											Pull Latest Denied List
										</button>
									) : (
										<button className={DeviceStyles.loadingBtn}>
											{deviceDataLoading === 'pullingDeniedList' ? 'Pulling Latest Denied List' : 'Pull Latest Denied List'}
										</button>
									)}
									
									{deviceDataLoading === '' ? (
										<button
											onClick={() => handlePostDeviceData('RebootSystem', 'rebootingSystem')}
											disabled={deviceDataLoading !== '' || !selectedWatcher}
											className={DeviceStyles.secondaryBtn}>
											Reboot System
										</button>
									) : (
										<button className={DeviceStyles.loadingBtn}>
											{deviceDataLoading === 'rebootingSystem' ? 'Rebooting System' : 'Reboot System'}
										</button>
									)}
								</div>
							</>
						) : (
							<p>No Watchers available for your account.</p>
						)}
					</div>
				</>
			)}
		</Content>
	);
};

export default DeviceScreen;
